import type { Mutation, QueryClient } from "@tanstack/vue-query";
import {
  SETUP_PROGRESS_MUTATION_KEY,
  type TourStatus,
} from "@/services/setup_progress";
import type { TourDefinition, TourStep } from "@/tours/types";
import { asList, findAnchor } from "@/composables/guidedTour/anchors";
import {
  display,
  hideStep,
  OPEN_MENU,
  popupOpen,
  tearDownDriver,
} from "@/composables/guidedTour/bubble";
import { loadedRegistry } from "@/composables/guidedTour/loadRegistry";
import {
  allowActionNavigation,
  run,
  session,
} from "@/composables/guidedTour/state";

// A save that starts this soon after the click is the one the click caused.
const SAVE_START_WINDOW_MS = 1500;
// No bubble shows while a save is followed, so nothing else would end it.
const FOLLOW_SAVE_MS = 30000;
// Longer than a dialog's or popover's close animation.
const REFUSED_CLOSE_MS = 500;

function isSave(
  mutation: Mutation<unknown, unknown, unknown, unknown> | undefined,
): mutation is Mutation<unknown, unknown, unknown, unknown> {
  return (
    !!mutation &&
    mutation.options.mutationKey?.[0] !== SETUP_PROGRESS_MUTATION_KEY[0]
  );
}

export function createSaveFollowing(engine: {
  queryClient: QueryClient;
  showStep: (tour: TourDefinition, index: number) => Promise<void>;
  stop: (
    tour: TourDefinition,
    step: number,
    status: TourStatus,
    reason?: "user" | "stuck",
  ) => void;
  giveUp: (
    tour: TourDefinition,
    index: number,
    why?: { title: string; description: string },
  ) => void;
}) {
  const { queryClient, showStep, stop, giveUp } = engine;

  function watchForAdvance(
    tour: TourDefinition,
    index: number,
    when: NonNullable<TourStep["advanceWhen"]>,
  ) {
    function advance(allowNavigation = true) {
      if (allowNavigation) allowActionNavigation();
      void showStep(tour, index + 1);
    }
    if (when.appears) {
      const appears = when.appears;
      if (findAnchor(appears)) {
        queueMicrotask(advance);
        return;
      }
      const observer = new MutationObserver(() => {
        if (findAnchor(appears)) advance();
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });
      display.stepListeners.push(() => observer.disconnect());
    }
    // Only the save the click started counts, not whatever else the app saves
    // around the same time.
    let clickedAt = 0;
    let saveId: number | null = null;
    let saveSettled = false;
    let closedAfterClick = false;
    // When a save was last refused: a form that closes at the click finishes
    // closing a moment after a quick refusal.
    let refusedAt = 0;
    function finish(outcome: "advance" | "stop") {
      // A save already allowed the one navigation it may cause; advancing
      // after it (the form closing later) mustn't allow another.
      if (outcome === "advance") advance(!saveSettled);
      else stop(tour, index, "closed");
    }
    function disarm() {
      clickedAt = 0;
      saveId = null;
      saveSettled = false;
    }
    // A save is reported a moment after the click, and some forms close
    // first. None by then: the click sent nothing (an invalid form).
    function settleClose() {
      if (saveId === null) finish("stop");
    }
    if (when.closes) {
      const closes = when.closes;
      const observer = new MutationObserver(() => {
        if (findAnchor(closes) || closedAfterClick) return;
        if (!when.clicked) {
          finish("advance");
        } else if (!clickedAt) {
          if (Date.now() - refusedAt < REFUSED_CLOSE_MS) {
            giveUp(tour, index, loadedRegistry().SAVE_REFUSED);
          } else {
            finish("stop");
          }
        } else if (saveSettled) {
          finish("advance");
        } else {
          closedAfterClick = true;
          const timer = setTimeout(settleClose, SAVE_START_WINDOW_MS);
          display.stepListeners.push(() => clearTimeout(timer));
        }
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });
      display.stepListeners.push(() => observer.disconnect());
    }
    if (when.clicked) {
      const selector = asList(when.clicked)
        .map((name) => `[data-tour="${name}"]`)
        .join(", ");
      // Capture phase, so the step moves on even if the control's own
      // handler stops the event or navigates.
      function onClick(event: MouseEvent) {
        if (!(event.target as Element | null)?.closest(selector)) return;
        if (!when.saved && !when.closes) {
          finish("advance");
          return;
        }
        // Another click while the save is on the way or done doesn't restart the wait.
        if (saveId !== null) return;
        // An Enter a moment ago may be waiting for a save: this click's is
        // the one, and it waits for the form to close.
        run.ownSaveClicks += 1;
        clickedAt = Date.now();
        saveId = null;
        saveSettled = false;
      }
      if (when.saved || when.closes) {
        const unsubscribe = queryClient.getMutationCache().subscribe((e) => {
          if (!clickedAt) return;
          const mutation = e.mutation;
          if (!isSave(mutation)) return;
          if (e.type === "added") {
            if (
              saveId === null &&
              Date.now() - clickedAt <= SAVE_START_WINDOW_MS
            ) {
              saveId = mutation.mutationId;
            }
            return;
          }
          if (e.type !== "updated" || mutation.mutationId !== saveId) return;
          if (e.action.type === "success") {
            saveSettled = true;
            // Saved, the app may open what was made (a new budget plan).
            allowActionNavigation(true);
            if (when.saved || closedAfterClick) finish("advance");
          }
          if (e.action.type === "error") {
            // Refused with the form still open: the user can try again.
            if (closedAfterClick) {
              giveUp(tour, index, loadedRegistry().SAVE_REFUSED);
            } else {
              refusedAt = Date.now();
              disarm();
            }
          }
        });
        display.stepListeners.push(unsubscribe);
      }
      document.addEventListener("click", onClick, { capture: true });
      display.stepListeners.push(() =>
        document.removeEventListener("click", onClick, { capture: true }),
      );
    }
  }

  // Enter in a field, or a later step's Save pressed early, submits without
  // the click a later step waits for: follow that save instead.
  function followFormSave(within: Element) {
    if (!run.currentStep) return;
    const { tour, index } = run.currentStep;
    const waits = tour.steps.findIndex(
      (step, i) =>
        i >= index &&
        !!step.advanceWhen?.closes &&
        !!within.closest(`[data-tour="${step.advanceWhen.closes}"]`),
    );
    if (waits !== -1) followSubmit(tour, index, waits);
  }
  // The step's own control is already followed by its click handler.
  function isOwnControl(control: Element | null | undefined): boolean {
    const clicked = run.currentStep
      ? run.currentStep.tour.steps[run.currentStep.index].advanceWhen?.clicked
      : undefined;
    return (
      !!control &&
      !!clicked &&
      asList(clicked).some((name) => control.closest(`[data-tour="${name}"]`))
    );
  }
  function onSubmit(event: SubmitEvent) {
    if (isOwnControl(event.submitter)) return;
    followFormSave(event.target as Element);
  }
  // Only a later step's own Save: the form's other buttons (a rule's preview)
  // start saves that aren't it.
  function onLaterSave(event: MouseEvent) {
    if (!run.currentStep) return;
    const { tour, index } = run.currentStep;
    const target = event.target as Element | null;
    if (isOwnControl(target)) return;
    const waits = tour.steps.findIndex(
      (step, i) =>
        i > index &&
        !!(step.advanceWhen?.closes || step.advanceWhen?.saved) &&
        asList(step.advanceWhen.clicked).some(
          (name) => !!target?.closest(`[data-tour="${name}"]`),
        ),
    );
    if (waits !== -1) followSubmit(tour, index, waits);
  }
  // Nothing changes unless a save starts: a form that only adds a row on
  // Enter (a competitor's spellings) keeps the user where they are.
  function followSubmit(tour: TourDefinition, index: number, waits: number) {
    const form = tour.steps[waits].advanceWhen?.closes;
    run.submitsPending += 1;
    let pending = true;
    function settle() {
      if (pending) run.submitsPending -= 1;
      pending = false;
    }
    const clicksBefore = run.ownSaveClicks;
    const stopWaiting = queryClient.getMutationCache().subscribe((e) => {
      if (e.type !== "added" || !isSave(e.mutation)) return;
      if (run.ownSaveClicks !== clicksBefore) {
        clearTimeout(timer);
        settle();
        return;
      }
      // Hiding the step for the save also ends this wait and its timer.
      follow(e.mutation.mutationId);
    });
    const timer = setTimeout(() => {
      stopWaiting();
      settle();
      if (run.submitsPending === 0 && form && !findAnchor(form)) {
        stop(tour, index, "closed");
      }
    }, SAVE_START_WINDOW_MS);
    display.stepListeners.push(stopWaiting, () => clearTimeout(timer), settle);

    function follow(saveId: number) {
      // Cancels a step still on its way in.
      session.stepToken += 1;
      hideStep();
      // The form is closing under the bubble; it comes back with the next
      // step, or this one if the save is refused.
      tearDownDriver();
      const unsubscribe = queryClient.getMutationCache().subscribe((e) => {
        // Gone with the step if the tour moved on or ended meanwhile.
        if (e.type !== "updated" || e.mutation.mutationId !== saveId) return;
        if (e.action.type === "success") {
          allowActionNavigation(true);
          // A form that stays open means something didn't go as planned.
          const form = tour.steps[waits].advanceWhen?.closes;
          if (!form || !findAnchor(form)) {
            void showStep(tour, waits + 1);
            return;
          }
          const closed = new MutationObserver(() => {
            if (findAnchor(form)) return;
            closed.disconnect();
            void showStep(tour, waits + 1);
          });
          closed.observe(document.body, { childList: true, subtree: true });
          display.stepListeners.push(() => closed.disconnect());
        }
        if (e.action.type === "error") void showStep(tour, index);
      });
      // No bubble while it saves, so Esc and a slow save still end it.
      function onEscape(event: KeyboardEvent) {
        // An open menu gets the Esc first; the form's own dialog doesn't,
        // since with no bubble Esc is the only way out.
        if (event.key === "Escape" && !popupOpen(OPEN_MENU.join(","))) {
          stop(tour, index, "closed");
        }
      }
      document.addEventListener("keydown", onEscape, { capture: true });
      const tooSlow = setTimeout(
        () => stop(tour, index, "closed", "stuck"),
        FOLLOW_SAVE_MS,
      );
      display.stepListeners.push(
        unsubscribe,
        () =>
          document.removeEventListener("keydown", onEscape, { capture: true }),
        () => clearTimeout(tooSlow),
      );
    }
  }

  return { watchForAdvance, onSubmit, onLaterSave };
}
